import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { reportsApi } from '../utils/reportsApi';
import { formatCurrency } from '../utils/helpers';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  CalendarDays, 
  CalendarRange, 
  TrendingUp, 
  TrendingDown,
  Banknote, 
  CreditCard, 
  RotateCcw,
  Download,
  FileSpreadsheet,
  FileText,
  BarChart3,
  PieChart,
  ChevronRight,
  Calendar as CalendarIcon,
  Euro,
  Receipt,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  X
} from 'lucide-react';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear } from 'date-fns';
import { de } from 'date-fns/locale';

// Quick period presets
const QUICK_PRESETS = [
  { id: 'today', label: 'Heute', icon: CalendarDays, getRange: () => ({ from: new Date(), to: new Date() }) },
  { id: 'week', label: 'Diese Woche', icon: CalendarRange, getRange: () => ({ from: startOfWeek(new Date(), { locale: de }), to: endOfWeek(new Date(), { locale: de }) }) },
  { id: 'month', label: 'Dieser Monat', icon: CalendarIcon, getRange: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }) },
];

function Berichte() {
  const [activePreset, setActivePreset] = useState('month');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: startOfMonth(new Date()), to: new Date() });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(null);

  useEffect(() => {
    loadPreset('month');
  }, []);

  const loadPreset = async (presetId) => {
    try {
      setLoading(true);
      setActivePreset(presetId);
      const data = await reportsApi.getPreset(presetId);
      setReport(data);
      
      // Update date range display
      const preset = QUICK_PRESETS.find(p => p.id === presetId);
      if (preset) {
        setDateRange(preset.getRange());
      }
    } catch (err) {
      console.error('Error loading report:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomPeriod = async (range) => {
    if (!range?.from || !range?.to) return;
    
    try {
      setLoading(true);
      setActivePreset('custom');
      setDateRange(range);
      const data = await reportsApi.getPeriod(
        format(range.from, 'yyyy-MM-dd'),
        format(range.to, 'yyyy-MM-dd')
      );
      setReport(data);
      setCalendarOpen(false);
    } catch (err) {
      console.error('Error loading custom period:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type) => {
    setExportLoading(type);
    // Simulate export - in production this would call an API
    await new Promise(resolve => setTimeout(resolve, 1500));
    setExportLoading(null);
    // Show success message or download file
  };

  // Calculate trend (mock - would come from API in production)
  const getTrend = () => {
    if (!report) return { value: 0, positive: true };
    // Mock trend calculation
    return { value: 12.5, positive: true };
  };

  const trend = getTrend();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Berichte & Analysen
          </h1>
          <p className="text-muted-foreground mt-1">
            Umsatzübersicht und detaillierte Auswertungen
          </p>
        </div>
        
        {/* Export Buttons */}
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleExport('csv')}
            disabled={exportLoading === 'csv' || !report}
            data-testid="export-csv-btn"
          >
            {exportLoading === 'csv' ? (
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                <FileSpreadsheet className="h-4 w-4 mr-2" />
              </motion.div>
            ) : (
              <FileSpreadsheet className="h-4 w-4 mr-2" />
            )}
            CSV Export
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleExport('datev')}
            disabled={exportLoading === 'datev' || !report}
            data-testid="export-datev-btn"
          >
            {exportLoading === 'datev' ? (
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                <FileText className="h-4 w-4 mr-2" />
              </motion.div>
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            DATEV Export
          </Button>
        </div>
      </div>

      {/* Period Selection */}
      <Card data-testid="period-selection-card">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-3">
            {/* Quick Presets */}
            {QUICK_PRESETS.map((preset) => {
              const Icon = preset.icon;
              return (
                <Button
                  key={preset.id}
                  variant={activePreset === preset.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => loadPreset(preset.id)}
                  className={activePreset === preset.id ? "bg-primary" : ""}
                  data-testid={`preset-${preset.id}`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {preset.label}
                </Button>
              );
            })}
            
            {/* Custom Date Range - Opens as Dialog */}
            <Button 
              variant={activePreset === 'custom' ? "default" : "outline"} 
              size="sm"
              className={activePreset === 'custom' ? "bg-primary" : ""}
              onClick={() => setCalendarOpen(true)}
              data-testid="custom-date-btn"
            >
              <CalendarRange className="h-4 w-4 mr-2" />
              {activePreset === 'custom' && dateRange?.from && dateRange?.to ? (
                <>
                  {format(dateRange.from, 'dd.MM.yy', { locale: de })} - {format(dateRange.to, 'dd.MM.yy', { locale: de })}
                </>
              ) : (
                'Zeitraum wählen'
              )}
            </Button>
            
            {/* Calendar Dialog */}
            <Dialog open={calendarOpen} onOpenChange={setCalendarOpen}>
              <DialogContent className="max-w-fit p-0">
                <DialogHeader className="p-6 pb-0">
                  <DialogTitle className="flex items-center gap-2">
                    <CalendarRange className="h-5 w-5" />
                    Zeitraum auswählen
                  </DialogTitle>
                  <DialogDescription>
                    Klicken Sie auf ein Startdatum und dann auf ein Enddatum
                  </DialogDescription>
                </DialogHeader>
                <div className="p-6 pt-4">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange?.from}
                    selected={dateRange}
                    onSelect={(range) => {
                      if (range?.from) {
                        setDateRange({ from: range.from, to: range.to || range.from });
                      }
                    }}
                    numberOfMonths={2}
                    locale={de}
                    className="rounded-lg border"
                  />
                </div>
                <DialogFooter className="p-6 pt-0 flex items-center justify-between sm:justify-between">
                  <div className="text-sm text-muted-foreground">
                    {dateRange?.from && dateRange?.to ? (
                      <>
                        Ausgewählt: <span className="font-medium text-foreground">{format(dateRange.from, 'dd.MM.yyyy', { locale: de })} - {format(dateRange.to, 'dd.MM.yyyy', { locale: de })}</span>
                      </>
                    ) : (
                      'Bitte Zeitraum auswählen'
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setCalendarOpen(false)}>
                      Abbrechen
                    </Button>
                    <Button 
                      onClick={() => {
                        if (dateRange?.from && dateRange?.to) {
                          loadCustomPeriod(dateRange);
                        }
                      }}
                      disabled={!dateRange?.from || !dateRange?.to}
                    >
                      Anwenden
                    </Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Current Period Display */}
            {dateRange?.from && dateRange?.to && (
              <div className="ml-auto text-sm text-muted-foreground">
                {format(dateRange.from, 'dd. MMMM yyyy', { locale: de })} — {format(dateRange.to, 'dd. MMMM yyyy', { locale: de })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading ? (
        <div className="space-y-6">
          {/* Stats Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i}>
                <CardContent className="pt-6">
                  <Skeleton className="h-4 w-24 mb-3" />
                  <Skeleton className="h-8 w-32 mb-2" />
                  <Skeleton className="h-3 w-20" />
                </CardContent>
              </Card>
            ))}
          </div>
          {/* Table Skeleton */}
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : report ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Revenue */}
            <Card className="relative overflow-hidden" data-testid="total-revenue-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Gesamtumsatz</p>
                    <p className="text-3xl font-bold mt-1">{formatCurrency(report.total_gross)}</p>
                    <div className="flex items-center mt-2 text-sm">
                      {trend.positive ? (
                        <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                          <ArrowUpRight className="h-3 w-3 mr-1" />
                          +{trend.value}%
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-red-500/10 text-red-600 border-red-500/20">
                          <ArrowDownRight className="h-3 w-3 mr-1" />
                          -{trend.value}%
                        </Badge>
                      )}
                      <span className="text-muted-foreground ml-2">vs. Vorperiode</span>
                    </div>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Euro className="h-6 w-6 text-primary" />
                  </div>
                </div>
                {/* Background decoration */}
                <div className="absolute -right-4 -bottom-4 h-24 w-24 rounded-full bg-primary/5" />
              </CardContent>
            </Card>

            {/* Cash */}
            <Card data-testid="cash-revenue-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Barzahlung</p>
                    <p className="text-3xl font-bold mt-1 text-amber-600">{formatCurrency(report.cash_total)}</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {report.total_gross > 0 ? ((report.cash_total / report.total_gross) * 100).toFixed(1) : 0}% vom Umsatz
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <Banknote className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card */}
            <Card data-testid="card-revenue-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Kartenzahlung</p>
                    <p className="text-3xl font-bold mt-1 text-emerald-600">{formatCurrency(report.card_total)}</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {report.total_gross > 0 ? ((report.card_total / report.total_gross) * 100).toFixed(1) : 0}% vom Umsatz
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
                    <CreditCard className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Transactions */}
            <Card data-testid="transactions-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Transaktionen</p>
                    <p className="text-3xl font-bold mt-1">{report.sales_count}</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      Ø {report.sales_count > 0 ? formatCurrency(report.total_gross / report.sales_count) : '€0,00'} pro Verkauf
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <Receipt className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs for different views */}
          <Tabs defaultValue="daily" className="space-y-4">
            <TabsList>
              <TabsTrigger value="daily" data-testid="tab-daily">
                <BarChart3 className="h-4 w-4 mr-2" />
                Tagesübersicht
              </TabsTrigger>
              <TabsTrigger value="vat" data-testid="tab-vat">
                <PieChart className="h-4 w-4 mr-2" />
                MwSt-Aufschlüsselung
              </TabsTrigger>
            </TabsList>

            {/* Daily Breakdown Tab */}
            <TabsContent value="daily">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Umsatz nach Tag
                  </CardTitle>
                  <CardDescription>
                    Detaillierte Aufschlüsselung der täglichen Verkäufe
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {report.daily_breakdown?.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                        <CalendarDays className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <p className="text-muted-foreground">Keine Verkäufe im gewählten Zeitraum</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="daily-breakdown-table">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Datum</th>
                            <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Verkäufe</th>
                            <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Umsatz</th>
                            <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Bar</th>
                            <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Karte</th>
                            <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Stornos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.daily_breakdown?.map((day, index) => (
                            <motion.tr 
                              key={day.date} 
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.03 }}
                              className="border-b hover:bg-muted/50 transition-colors"
                            >
                              <td className="py-3 px-4">
                                <div className="font-medium">
                                  {new Date(day.date + 'T00:00:00').toLocaleDateString('de-DE', {
                                    weekday: 'short',
                                    day: '2-digit',
                                    month: '2-digit',
                                  })}
                                </div>
                              </td>
                              <td className="text-right py-3 px-4">
                                <Badge variant="secondary">{day.sales}</Badge>
                              </td>
                              <td className="text-right py-3 px-4">
                                <span className="font-bold">{formatCurrency(day.gross)}</span>
                              </td>
                              <td className="text-right py-3 px-4">
                                <span className="text-amber-600">{formatCurrency(day.cash)}</span>
                              </td>
                              <td className="text-right py-3 px-4">
                                <span className="text-emerald-600">{formatCurrency(day.card)}</span>
                              </td>
                              <td className="text-right py-3 px-4">
                                {day.refunds > 0 ? (
                                  <Badge variant="destructive">{day.refunds}</Badge>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )}
                              </td>
                            </motion.tr>
                          ))}
                        </tbody>
                        {/* Footer with totals */}
                        <tfoot>
                          <tr className="bg-muted/30 font-semibold">
                            <td className="py-3 px-4">Gesamt</td>
                            <td className="text-right py-3 px-4">{report.sales_count}</td>
                            <td className="text-right py-3 px-4">{formatCurrency(report.total_gross)}</td>
                            <td className="text-right py-3 px-4 text-amber-600">{formatCurrency(report.cash_total)}</td>
                            <td className="text-right py-3 px-4 text-emerald-600">{formatCurrency(report.card_total)}</td>
                            <td className="text-right py-3 px-4 text-red-600">{report.refund_count}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* VAT Tab */}
            <TabsContent value="vat">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="h-5 w-5" />
                    MwSt-Übersicht
                  </CardTitle>
                  <CardDescription>
                    Aufschlüsselung nach Mehrwertsteuersätzen
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Netto */}
                    <div className="p-6 rounded-xl bg-muted/30 border">
                      <p className="text-sm font-medium text-muted-foreground mb-2">Netto-Umsatz</p>
                      <p className="text-3xl font-bold">{formatCurrency(report.total_net)}</p>
                    </div>
                    
                    {/* MwSt Total */}
                    <div className="p-6 rounded-xl bg-muted/30 border">
                      <p className="text-sm font-medium text-muted-foreground mb-2">MwSt gesamt</p>
                      <p className="text-3xl font-bold">{formatCurrency(report.total_vat)}</p>
                    </div>
                    
                    {/* VAT Breakdown */}
                    <div className="p-6 rounded-xl bg-muted/30 border">
                      <p className="text-sm font-medium text-muted-foreground mb-3">Nach Steuersatz</p>
                      <div className="space-y-2">
                        {Object.entries(report.vat_breakdown || {}).map(([rate, amount]) => (
                          <div key={rate} className="flex items-center justify-between">
                            <Badge variant="outline">{rate}%</Badge>
                            <span className="font-semibold">{formatCurrency(amount)}</span>
                          </div>
                        ))}
                        {Object.keys(report.vat_breakdown || {}).length === 0 && (
                          <p className="text-muted-foreground text-sm">Keine MwSt-Daten</p>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Refunds Section */}
                  {report.refund_count > 0 && (
                    <div className="mt-6 p-4 rounded-xl bg-red-500/5 border border-red-500/20">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-red-500/10 flex items-center justify-center">
                          <RotateCcw className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-red-600">Stornierungen</p>
                          <p className="text-sm text-muted-foreground">
                            {report.refund_count} Stornos im Wert von {formatCurrency(report.refund_total)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">Wählen Sie einen Zeitraum für den Bericht</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default Berichte;
